import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook for playing scrubbing sound effects during photo navigation.
 * Uses Web Audio API for low-latency, high-performance audio playback.
 *
 * Features:
 * - Generates a soft "tick" sound procedurally (no audio files needed)
 * - Pitch varies based on playback speed (higher speed = higher pitch)
 * - Volume scales with speed
 * - Automatic throttling to prevent sound overlap at high speeds
 */
export function useScrubSound() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastPlayTimeRef = useRef<number>(0);
  const enabledRef = useRef<boolean>(true);

  // Initialize AudioContext lazily (must be after user interaction)
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) {
          console.warn('[useScrubSound] AudioContext not available, sound disabled');
          enabledRef.current = false;
          return null;
        }
        audioContextRef.current = new AudioCtx();
      } catch (e) {
        console.warn('[useScrubSound] Failed to create AudioContext:', e);
        enabledRef.current = false;
        return null;
      }
    }
    return audioContextRef.current;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  /**
   * Play a soft tick sound for scrubbing feedback
   * @param speed - Current playback speed (fps), affects pitch and volume
   */
  const playTick = useCallback((speed: number = 30) => {
    if (!enabledRef.current) return;

    const now = performance.now();
    const absSpeed = Math.abs(speed);

    // Dynamic rate limiting based on velocity:
    // - Low speed (<100fps): 20ms interval (~50 ticks/sec) - clear individual ticks
    // - Medium speed (100-500fps): 10ms interval (~100 ticks/sec) - rapid feedback
    // - High speed (>500fps): 5ms interval (~200 ticks/sec) - max response
    let minInterval: number;
    if (absSpeed > 500) {
      minInterval = 5;
    } else if (absSpeed > 100) {
      minInterval = 10;
    } else {
      minInterval = 20;
    }

    if (now - lastPlayTimeRef.current < minInterval) return;
    lastPlayTimeRef.current = now;

    try {
      const ctx = getAudioContext();
      if (!ctx) return; // AudioContext not available, skip silently

      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const currentTime = ctx.currentTime;

      // 1ms metallic iron click
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      // Sawtooth wave at high freq = metallic/iron sound
      oscillator.type = 'sawtooth';
      oscillator.frequency.value = 3200; // Higher = more metallic/iron

      gainNode.gain.setValueAtTime(0.05, currentTime);
      gainNode.gain.setValueAtTime(0, currentTime + 0.001);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(currentTime);
      oscillator.stop(currentTime + 0.001);

      oscillator.onended = () => {
        oscillator.disconnect();
        gainNode.disconnect();
      };
    } catch (e) {
      // Silently fail - audio is enhancement, not critical
      console.debug('[useScrubSound] Audio playback failed:', e);
    }
  }, [getAudioContext]);

  /**
   * Enable/disable sound effects
   */
  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled;
  }, []);

  /**
   * Check if sound is enabled
   */
  const isEnabled = useCallback(() => enabledRef.current, []);

  return {
    playTick,
    setEnabled,
    isEnabled,
  };
}
